import React from 'react';
import { Box, Button, Dialog, DialogContent, Grid } from '@mui/material';
import { FontAttributes, fontSchema } from '@loyalty/shared-common/template-api-client';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { CustomFontForm, GoogleFontForm } from './CustomFontDialogForms';
import { Loader } from '../../../../shared/layout/Loader';
import { Title } from '../../../../shared/layout/Title';
import { useAddFont, useUpdateFont } from '../../../hooks';
import { useJoiResolver } from '../../../../shared/forms/use-joi-resolver';

export type CustomFontDialogMode = 'add' | 'close' | 'edit';

interface Props {
  mode: CustomFontDialogMode;
  onClose: () => void;
  onAdd?: (font: FontAttributes) => void;
  font?: FontAttributes;
}

const useStyles = makeStyles(() => ({
  root: {
    color: '#666666',
  },
  row: {
    marginBottom: '20px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  link: {
    color: '#61c1db',
  },
  button: {
    margin: '10px !important',
  },
}));

export function CustomFontDialog({ mode, onClose, onAdd, font }: Props) {
  // Use React Query mutations
  const { mutateAsync: addFont, isPending: isAddingFont } = useAddFont();
  const { mutateAsync: updateFont, isPending: isUpdatingFont } = useUpdateFont();

  const { t } = useTranslation();
  const classes = useStyles();

  const loading = isAddingFont || isUpdatingFont;

  const resolver = useJoiResolver(fontSchema);
  const form = useForm<FontAttributes>({
    mode: 'onChange',
    resolver,
    defaultValues: font || { type: 'google' },
  });
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = form;

  const fontType = watch('type');

  const onSubmit = async (font: FontAttributes) => {
    if (mode === 'add') {
      await addFont(font);
    } else {
      await updateFont(font);
    }

    onAdd && onAdd(font);
    onClose();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog onClose={onClose} open={mode !== 'close'} fullWidth={true} maxWidth="sm">
          <DialogContent className={classes.root}>
            <Grid container>
              <Grid container item justifyContent="center">
                <Box textAlign="center">
                  <Title>
                    {mode === 'add'
                      ? t('editor.custom-font.dialog.create.title')
                      : t('editor.custom-font.dialog.edit.title')}
                  </Title>
                  {mode === 'add' && <p>{t('editor.custom-font.dialog.create.description')}</p>}
                </Box>
              </Grid>

              <Box className={classes.row}>
                <Button
                  type="button"
                  color={fontType === 'google' ? 'secondary' : 'info'}
                  onClick={() => {
                    setValue('type', 'google');
                  }}
                  style={{ borderBottomRightRadius: 0, borderTopRightRadius: 0 }}
                >
                  {t('editor.custom-fonts.google-font')}
                </Button>
                <Button
                  type="button"
                  color={fontType === 'custom' ? 'secondary' : 'info'}
                  onClick={() => {
                    setValue('type', 'custom');
                  }}
                  style={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0 }}
                >
                  {t('editor.custom-fonts.custom-font')}
                </Button>
              </Box>

              <Grid container item justifyContent="center" className={classes.row}>
                {fontType === 'google' ? <GoogleFontForm /> : <CustomFontForm />}
              </Grid>

              <Box className={classes.row}>
                <Button type="button" color="info" onClick={onClose} className={classes.button}>
                  {t('editor.custom-fonts.button.cancel')}
                </Button>
                <Button
                  type="submit"
                  color="success"
                  disabled={loading || !isValid}
                  onClick={handleSubmit(onSubmit)}
                  className={classes.button}
                >
                  {mode === 'add'
                    ? t('editor.custom-fonts.button.add-font')
                    : t('editor.custom-fonts.button.update-font')}
                  {loading && <Loader />}
                </Button>
              </Box>
            </Grid>
          </DialogContent>
        </Dialog>
      </form>
    </FormProvider>
  );
}
